import { Box, Card, CardContent, CircularProgress, Tab, Tabs, Typography } from '@mui/material'

import { CARD_CONTENT_COMPACT } from '@/theme/responsive'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch } from '@/store'
import { apiSlice } from '@/store/api/apiSlice'
import { usePreference } from '@/hooks/usePreference'
import SettingsRoleListView from '@/pages/settings/SettingsRoleListView'
import SettingsAccountListView from '@/pages/settings/SettingsAccountListView'
import SettingsLogHistoryView from '@/pages/settings/SettingsLogHistoryView'

const TAB_CONFIG = [
  { key: 'roles', label: 'Roles', path: '/settings/roles' },
  { key: 'users', label: 'Users', path: '/settings/users' },
  { key: 'log-history', label: 'Log History', path: '/settings/log-history' },
] as const

type SettingsTabKey = typeof TAB_CONFIG[number]['key']

const isValidTabKey = (k: string): k is SettingsTabKey =>
  TAB_CONFIG.some((t) => t.key === k)

// Mỗi tab map sang đúng tag RTK Query để invalidate khi đổi tab → fresh fetch.
const TAB_TAGS: Record<SettingsTabKey, string> = {
  roles: 'SettingsRole',
  users: 'SettingsUser',
  'log-history': 'SettingsLog',
}

export default function SettingsPage() {
  const location = useLocation()
  const navigate = useNavigate()

  // Per-user "last opened tab" persistence (generic preferences registry).
  const navPref = usePreference<{ lastTab: string }>('settings', 'navigation', undefined, { refetchOnMount: true })

  const activeKey: SettingsTabKey = useMemo(() => {
    const p = location.pathname
    const found = TAB_CONFIG.find((t) => p.startsWith(t.path))
    return found ? found.key : 'roles'
  }, [location.pathname])

  // Bare/invalid sub-tab route → restore last opened tab (nếu pref đã fetch
  // xong). Fallback về 'roles' nếu chưa lưu / tab lưu không hợp lệ.
  useEffect(() => {
    const hasValidSubTab = TAB_CONFIG.some((t) => location.pathname.startsWith(t.path))
    if (hasValidSubTab) return
    if (navPref.isLoading || navPref.isFetching) return
    const saved = navPref.value?.lastTab
    const target = saved && isValidTabKey(saved) ? saved : 'roles'
    navigate(`/settings/${target}`, { replace: true })
  }, [location.pathname, navigate, navPref.isLoading, navPref.isFetching, navPref.value])

  // Mỗi lần đổi tab → invalidate cache tag tương ứng + 1 frame loading transition
  // để API tab đích luôn refetch fresh, đồng thời hiển thị Loading state liền mạch.
  const dispatch = useAppDispatch()
  const isInitialMountRef = useRef(true)
  const [mountedKey, setMountedKey] = useState<SettingsTabKey | null>(activeKey)
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      setMountedKey(activeKey)
      return
    }
    dispatch(apiSlice.util.invalidateTags([TAB_TAGS[activeKey] as never]))
    setMountedKey(null)
    const raf = requestAnimationFrame(() => setMountedKey(activeKey))
    return () => cancelAnimationFrame(raf)
  }, [activeKey, dispatch])

  // Auto-save tab hiện tại (debounce 400ms) để click nhanh không spam BE.
  useEffect(() => {
    if (navPref.isLoading || navPref.isFetching) return
    const urlTab = location.pathname.split('/').filter(Boolean)[1]
    if (!isValidTabKey(urlTab ?? '')) return
    if (navPref.value?.lastTab === urlTab) return
    void navPref.patch({ lastTab: urlTab }).catch(() => undefined)
  }, [location.pathname, navPref.value?.lastTab, navPref.patch])

  // Resolve-first: URL chưa có sub-tab hợp lệ (đang đọc DB + chờ restore) → hiện
  // loading, KHÔNG render Page default. Có Page đúng trên URL mới render.
  if (!isValidTabKey(location.pathname.split('/').filter(Boolean)[1] ?? ''))
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={24} />
      </Box>
    )

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          SETTINGS
        </Typography>
      </Stack> */}

      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeKey}
          onChange={(_, value) => {
            const tab = TAB_CONFIG.find((t) => t.key === value)
            if (tab) {
              void navPref.patch({ lastTab: value }).catch(() => undefined)
              navigate(tab.path)
            }
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 34,
            '& .MuiTabs-indicator': {
              height: 2.5,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              position: 'relative',
              minHeight: 34,
              minWidth: 0,
              py: 0.25,
              px: 1.5,
              fontSize: '0.78rem',
              fontWeight: 600,
              letterSpacing: 0.15,
              textTransform: 'none',
              color: 'text.secondary',
              borderRadius: '6px 6px 0 0',
              transition: 'color 140ms ease, background-color 140ms ease',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover',
              },
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              },
              '&:not(:last-of-type)::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 16,
                width: '1px',
                bgcolor: 'divider',
                opacity: 0.6,
                transition: 'opacity 140ms ease',
              },
              '&:hover::after, &.Mui-selected::after': { opacity: 0 },
            },
          }}
        >
          {TAB_CONFIG.map((t) => (
            <Tab key={t.key} label={t.label} value={t.key} />
          ))}
        </Tabs>

        <CardContent sx={CARD_CONTENT_COMPACT}>
          {mountedKey === null ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
              <CircularProgress size={28} />
            </Box>
          ) : mountedKey === 'roles' ? (
            <SettingsRoleListView />
          ) : mountedKey === 'users' ? (
            <SettingsAccountListView />
          ) : mountedKey === 'log-history' ? (
            <SettingsLogHistoryView />
          ) : (
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Unknown tab
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}

